import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { OverlayWindowScaleMode } from './types';

export interface OverlayWindowProps {
  /**
   * Ref to the overlay-window *root* element. The root is positioned and sized
   * to match the video content box (see overlay-window/index.ts); the window's
   * `left`/`top` are relative to it, and it's the scale/alignment origin.
   */
  targetRef: React.RefObject<HTMLElement | null>;
  /**
   * Ref to the element the window is *bounded* to while dragging/resizing.
   * Defaults to the root (`targetRef`). Pass a larger element — e.g. the player
   * stage — to let the window move beyond the video content box but stay within
   * the player.
   */
  boundsRef?: React.RefObject<HTMLElement | null>;
  backgroundOpacity?: number;
  blur?: number;
  initialPosition?: { x: number; y: number };
  /**
   * Initial (unscaled) size of the window in px, at scale = 1. Scaled by
   * `--overlay-scale` like the default size, so it still grows in fullscreen.
   * Omit to use the stylesheet default (240×160).
   */
  initialSize?: { width: number; height: number };
  /**
   * Minimum (unscaled) size the user can resize the window to, in px at
   * scale = 1. Defaults to a small floor so the window can't collapse.
   */
  minSize?: { width: number; height: number };
  /**
   * Maximum (unscaled) size the user can resize the window to, in px at
   * scale = 1. Defaults to unbounded.
   */
  maxSize?: { width: number; height: number };
  scaleMode?: OverlayWindowScaleMode;
  /**
   * If true, the window's initial horizontal position is aligned so its content
   * (body) left edge lines up with the scale reference element's left edge —
   * e.g. the seek bar. Use with `scaleMode: 'seekbar'` so the body spans and
   * aligns with the seek bar regardless of whether the seek bar is centered
   * (it isn't on small players, where the duration label is hidden). Applied
   * once at mount; the user can still drag afterwards.
   */
  alignXToReference?: boolean;
  className?: string;
  onClose?: () => void;
  /** Called once after the window has mounted and the API is available. */
  onMounted?: () => void;
  children?: React.ReactNode;
}

export interface OverlayWindowAPI {
  /** Set the position as a fraction (0–1) of the available space. */
  setPosition: (x: number, y: number) => void;
  /** Read the current position fraction. */
  getPosition: () => { x: number; y: number };
}

const clamp = (v: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, v));

/** Which edges a resize gesture moves. */
interface ResizeDir {
  l?: boolean;
  r?: boolean;
  t?: boolean;
  b?: boolean;
}

// The eight resize hit-zones (4 edges + 4 corners). `name` is the CSS modifier;
// `dir` is the set of edges that move when that zone is dragged.
const RESIZE_HANDLES: ReadonlyArray<{ name: string; dir: ResizeDir }> = [
  { name: 'n', dir: { t: true } },
  { name: 's', dir: { b: true } },
  { name: 'e', dir: { r: true } },
  { name: 'w', dir: { l: true } },
  { name: 'ne', dir: { t: true, r: true } },
  { name: 'nw', dir: { t: true, l: true } },
  { name: 'se', dir: { b: true, r: true } },
  { name: 'sw', dir: { b: true, l: true } },
];

/**
 * A draggable, auto-scaling window layered over the player's video content
 * area. The whole surface is the drag handle (the close button is excluded);
 * position is kept as a fraction of the available space so it survives resize
 * and fullscreen, and dragging is clamped to the video content box. The window
 * hides itself while an annotation or the annotation editor is showing.
 */
export const OverlayWindow = forwardRef<OverlayWindowAPI, OverlayWindowProps>(
  function OverlayWindow(
    {
      targetRef,
      boundsRef,
      backgroundOpacity = 0.6,
      blur = 0,
      initialPosition,
      initialSize,
      minSize,
      maxSize,
      scaleMode = 'video',
      alignXToReference = false,
      className,
      onClose,
      onMounted,
      children,
    },
    ref,
  ) {
    const windowRef = useRef<HTMLDivElement>(null);
    // Current (unscaled) base size, driving --overlay-window-base-w/h. Starts
    // from initialSize (or the stylesheet default once known) and changes when
    // the user resizes. Kept in state so a resize re-renders the size vars.
    const [baseSize, setBaseSize] = useState<{ width: number; height: number } | null>(
      initialSize ?? null,
    );
    // Resize bounds (unscaled px). A small floor stops the window collapsing.
    const minW = minSize?.width ?? 60;
    const minH = minSize?.height ?? 40;
    const maxW = maxSize?.width ?? Infinity;
    const maxH = maxSize?.height ?? Infinity;
    // Position as a fraction (0–1) of the available space. Initialised once.
    const posRef = useRef<{ x: number; y: number }>({
      x: clamp(initialPosition?.x ?? 0.5, 0, 1),
      y: clamp(initialPosition?.y ?? 0.5, 0, 1),
    });
    // Width of the scaling reference captured at mount (scale = 1 baseline).
    const referenceWidthRef = useRef<number | null>(null);
    // Whether the one-time horizontal alignment to the reference has run.
    const didAlignRef = useRef(false);
    const dragRef = useRef<{
      startX: number;
      startY: number;
      startLeft: number;
      startTop: number;
    } | null>(null);
    // Active resize gesture: pointer origin, base size + window box at gesture
    // start, and which edges are being dragged.
    const resizeRef = useRef<{
      startX: number;
      startY: number;
      startW: number;
      startH: number;
      startLeftPx: number;
      startTopPx: number;
      // Drag-bounds box, in root coordinates.
      boundsLeft: number;
      boundsTop: number;
      boundsW: number;
      boundsH: number;
      scale: number;
      dir: ResizeDir;
    } | null>(null);
    // Latest applied scale, so a resize can convert screen-px deltas to base px.
    const scaleRef = useRef(1);
    const [hidden, setHidden] = useState(false);

    // Resolve the element whose width drives proportional scaling.
    const getReferenceEl = useCallback((): HTMLElement | null => {
      const root = targetRef.current;
      if (!root) return null;
      if (scaleMode === 'seekbar') {
        const wrapper =
          root.closest('.kollab-player-wrapper') ?? root.parentElement;
        const seekBar = wrapper?.querySelector(
          '.kollab-player-seek-bar',
        ) as HTMLElement | null;
        if (seekBar) return seekBar;
        // Fall back to the video content box if the seek bar isn't present.
      }
      return root;
    }, [targetRef, scaleMode]);

    // The drag-bounds box (defaults to the root), expressed in root-relative
    // coordinates — the same coordinate space as the window's `left`/`top`.
    // When `boundsRef` is a larger element (e.g. the player stage), this lets
    // the window move beyond the root (video content box) but stay in bounds.
    const getBounds = useCallback((): {
      left: number;
      top: number;
      width: number;
      height: number;
    } | null => {
      const root = targetRef.current;
      if (!root) return null;
      const rootRect = root.getBoundingClientRect();
      const boundsEl = boundsRef?.current ?? root;
      const bRect = boundsEl.getBoundingClientRect();
      return {
        left: bRect.left - rootRect.left,
        top: bRect.top - rootRect.top,
        width: bRect.width,
        height: bRect.height,
      };
    }, [targetRef, boundsRef]);

    // Convert the stored fraction into pixel left/top, clamped to the bounds.
    const applyPosition = useCallback(() => {
      const win = windowRef.current;
      const b = getBounds();
      if (!win || !b) return;
      const winRect = win.getBoundingClientRect();
      const availW = Math.max(0, b.width - winRect.width);
      const availH = Math.max(0, b.height - winRect.height);
      win.style.left = b.left + clamp(posRef.current.x * availW, 0, availW) + 'px';
      win.style.top = b.top + clamp(posRef.current.y * availH, 0, availH) + 'px';
    }, [getBounds]);

    // Recompute the scale from the reference width, then re-place the window.
    const applyScale = useCallback(() => {
      const win = windowRef.current;
      if (!win) return;
      const refEl = getReferenceEl();
      const w = refEl?.clientWidth ?? 0;
      if (referenceWidthRef.current == null && w > 0) {
        referenceWidthRef.current = w;
      }
      const reference = referenceWidthRef.current;
      const scale = reference && reference > 0 && w > 0 ? w / reference : 1;
      scaleRef.current = scale;
      win.style.setProperty('--overlay-scale', String(scale));
      applyPosition();
    }, [getReferenceEl, applyPosition]);

    // One-time: align the window's content (body) left edge to the reference
    // element's left edge, so the body lines up with the seek bar even when the
    // seek bar isn't centered (small players hide the duration label). The body
    // is inset from the window by its padding, so offset by paddingLeft.
    const alignXToReferenceOnce = useCallback(() => {
      if (didAlignRef.current) return;
      const win = windowRef.current;
      const root = targetRef.current;
      const refEl = getReferenceEl();
      const b = getBounds();
      if (!win || !root || !refEl || refEl === root || !b) return;
      const rootRect = root.getBoundingClientRect();
      const refRect = refEl.getBoundingClientRect();
      const winRect = win.getBoundingClientRect();
      const padLeft = parseFloat(getComputedStyle(win).paddingLeft) || 0;
      // Window left (root coords) that lines the body up with the reference.
      const desiredLeft = refRect.left - rootRect.left - padLeft;
      const availW = Math.max(0, b.width - winRect.width);
      // Express as a fraction across the bounds (applyPosition adds b.left back).
      posRef.current.x =
        availW > 0 ? clamp((desiredLeft - b.left) / availW, 0, 1) : 0;
      didAlignRef.current = true;
      applyPosition();
    }, [targetRef, getReferenceEl, getBounds, applyPosition]);

    // Capture the reference width, set the initial scale/position, and keep
    // both in sync when the reference (and root) resize.
    useLayoutEffect(() => {
      applyScale();
      if (alignXToReference) alignXToReferenceOnce();
      if (typeof ResizeObserver === 'undefined') return;
      const ro = new ResizeObserver(() => applyScale());
      const refEl = getReferenceEl();
      if (refEl) ro.observe(refEl);
      // Also observe the root so the window re-clamps when the content box
      // changes independently of the (possibly seek-bar) scale reference.
      const root = targetRef.current;
      if (root && root !== refEl) ro.observe(root);
      return () => ro.disconnect();
    }, [applyScale, getReferenceEl, targetRef, alignXToReference, alignXToReferenceOnce]);

    // Hide while any annotation viewer/editor root is present in the wrapper.
    useEffect(() => {
      const root = targetRef.current;
      if (!root) return;
      const wrapper = root.closest('.kollab-player-wrapper') ?? root.parentElement;
      if (!wrapper) return;
      const check = () =>
        setHidden(!!wrapper.querySelector('[data-annotation-root]'));
      check();
      if (typeof MutationObserver === 'undefined') return;
      const obs = new MutationObserver(check);
      obs.observe(wrapper, { childList: true });
      return () => obs.disconnect();
    }, [targetRef]);

    // Re-place the window when it becomes visible again (it had no size while
    // display:none, so its position couldn't be computed).
    useLayoutEffect(() => {
      if (!hidden) applyScale();
    }, [hidden, applyScale]);

    // After a resize changes the base size, re-clamp the position so the
    // (now larger/smaller) window stays inside the root.
    useLayoutEffect(() => {
      applyPosition();
    }, [baseSize, applyPosition]);

    useImperativeHandle(
      ref,
      () => ({
        setPosition: (x: number, y: number) => {
          posRef.current = { x: clamp(x, 0, 1), y: clamp(y, 0, 1) };
          applyPosition();
        },
        getPosition: () => ({ ...posRef.current }),
      }),
      [applyPosition],
    );

    useEffect(() => {
      onMounted?.();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
      // The close button and the resize handle are not drag handles.
      const t = e.target as HTMLElement;
      if (t.closest('.kollab-overlay-window-close')) return;
      if (t.closest('.kollab-overlay-window-resize')) return;
      const win = windowRef.current;
      const root = targetRef.current;
      if (!win || !root) return;
      const rootRect = root.getBoundingClientRect();
      const winRect = win.getBoundingClientRect();
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startLeft: winRect.left - rootRect.left,
        startTop: winRect.top - rootRect.top,
      };
      try {
        win.setPointerCapture?.(e.pointerId);
      } catch {
        // setPointerCapture can throw for synthetic/non-active pointers (e.g.
        // in jsdom); dragging works without capture, so ignore.
      }
    };

    const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      const win = windowRef.current;
      const b = getBounds();
      if (!drag || !win || !b) return;
      const winRect = win.getBoundingClientRect();
      const availW = Math.max(0, b.width - winRect.width);
      const availH = Math.max(0, b.height - winRect.height);
      // startLeft/Top and left/top are in root coords; clamp to the bounds box.
      const left = clamp(drag.startLeft + (e.clientX - drag.startX), b.left, b.left + availW);
      const top = clamp(drag.startTop + (e.clientY - drag.startY), b.top, b.top + availH);
      win.style.left = left + 'px';
      win.style.top = top + 'px';
      posRef.current = {
        x: availW > 0 ? (left - b.left) / availW : 0,
        y: availH > 0 ? (top - b.top) / availH : 0,
      };
    };

    const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragRef.current) return;
      dragRef.current = null;
      windowRef.current?.releasePointerCapture?.(e.pointerId);
    };

    // Resize gesture — handlers live on the edge/corner hit-zones, which capture
    // the pointer so subsequent move/up route to them (and the window drag is
    // skipped via the `.kollab-overlay-window-resize` guard above). Sizes are
    // tracked in unscaled base px so min/max and persistence are scale-stable.
    const onResizePointerDown = (
      e: React.PointerEvent<HTMLDivElement>,
      dir: ResizeDir,
    ) => {
      const win = windowRef.current;
      const root = targetRef.current;
      const b = getBounds();
      if (!win || !root || !b) return;
      const scale = scaleRef.current || 1;
      const rootRect = root.getBoundingClientRect();
      const winRect = win.getBoundingClientRect();
      resizeRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startW: baseSize?.width ?? winRect.width / scale,
        startH: baseSize?.height ?? winRect.height / scale,
        startLeftPx: winRect.left - rootRect.left,
        startTopPx: winRect.top - rootRect.top,
        boundsLeft: b.left,
        boundsTop: b.top,
        boundsW: b.width,
        boundsH: b.height,
        scale,
        dir,
      };
      try {
        e.currentTarget.setPointerCapture?.(e.pointerId);
      } catch {
        // Ignore — resizing works without capture (e.g. in jsdom).
      }
    };

    const onResizePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
      const r = resizeRef.current;
      if (!r) return;
      const { scale, dir } = r;
      const dx = e.clientX - r.startX;
      const dy = e.clientY - r.startY;
      const startWpx = r.startW * scale;
      const startHpx = r.startH * scale;
      // Min/max in scaled px. The moving edge is clamped against both the
      // min/max size AND the bounds, so the window can never grow past the
      // player edge (not just be repositioned inside it).
      const minWpx = minW * scale;
      const maxWpx = maxW * scale;
      const minHpx = minH * scale;
      const maxHpx = maxH * scale;
      const boundsRight = r.boundsLeft + r.boundsW;
      const boundsBottom = r.boundsTop + r.boundsH;

      // X axis: a right-edge drag moves the right edge (left anchored); a
      // left-edge drag moves the left edge (right anchored). The moving edge is
      // clamped so it stays within the bounds and within min/max width.
      let leftPx = r.startLeftPx;
      let widthPx = startWpx;
      if (dir.r) {
        const rightPx = clamp(
          r.startLeftPx + startWpx + dx,
          r.startLeftPx + minWpx,
          Math.min(boundsRight, r.startLeftPx + maxWpx),
        );
        widthPx = rightPx - leftPx;
      } else if (dir.l) {
        const rightFixedPx = r.startLeftPx + startWpx;
        leftPx = clamp(
          r.startLeftPx + dx,
          Math.max(r.boundsLeft, rightFixedPx - maxWpx),
          rightFixedPx - minWpx,
        );
        widthPx = rightFixedPx - leftPx;
      }

      // Y axis: bottom-edge moves the bottom (top anchored); top-edge moves the
      // top (bottom anchored). Same bounds + min/max clamping as X.
      let topPx = r.startTopPx;
      let heightPx = startHpx;
      if (dir.b) {
        const bottomPx = clamp(
          r.startTopPx + startHpx + dy,
          r.startTopPx + minHpx,
          Math.min(boundsBottom, r.startTopPx + maxHpx),
        );
        heightPx = bottomPx - topPx;
      } else if (dir.t) {
        const bottomFixedPx = r.startTopPx + startHpx;
        topPx = clamp(
          r.startTopPx + dy,
          Math.max(r.boundsTop, bottomFixedPx - maxHpx),
          bottomFixedPx - minHpx,
        );
        heightPx = bottomFixedPx - topPx;
      }

      // Store position as a fraction so applyPosition (run from the baseSize
      // layout effect) re-places the window consistently.
      const availW = Math.max(0, r.boundsW - widthPx);
      const availH = Math.max(0, r.boundsH - heightPx);
      posRef.current = {
        x: availW > 0 ? (leftPx - r.boundsLeft) / availW : 0,
        y: availH > 0 ? (topPx - r.boundsTop) / availH : 0,
      };
      setBaseSize({ width: widthPx / scale, height: heightPx / scale });
    };

    const endResize = (e: React.PointerEvent<HTMLDivElement>) => {
      if (!resizeRef.current) return;
      resizeRef.current = null;
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    };

    return (
      <div
        ref={windowRef}
        className={
          'kollab-overlay-window' +
          (hidden ? ' kollab-overlay-window--hidden' : '') +
          (className ? ' ' + className : '')
        }
        style={
          {
            '--overlay-window-bg': `rgba(20, 20, 20, ${backgroundOpacity})`,
            ...(baseSize
              ? {
                  '--overlay-window-base-w': `${baseSize.width}px`,
                  '--overlay-window-base-h': `${baseSize.height}px`,
                }
              : {}),
            backdropFilter: blur ? `blur(${blur}px)` : undefined,
            WebkitBackdropFilter: blur ? `blur(${blur}px)` : undefined,
          } as React.CSSProperties
        }
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <button
          type="button"
          className="kollab-overlay-window-close img-button"
          aria-label="Close"
          title="Close"
          onClick={() => onClose?.()}
        >
          <img src="img/third_party/fontawesome/x-solid.svg" alt="" />
        </button>
        <div className="kollab-overlay-window-body">{children}</div>
        {RESIZE_HANDLES.map(({ name, dir }) => (
          <div
            key={name}
            className={`kollab-overlay-window-resize kollab-overlay-window-resize--${name}`}
            aria-hidden="true"
            onPointerDown={(e) => onResizePointerDown(e, dir)}
            onPointerMove={onResizePointerMove}
            onPointerUp={endResize}
            onPointerCancel={endResize}
          />
        ))}
      </div>
    );
  },
);
